import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Text from '../../../../../shared/_TBComponents/Text';
import IconActionButton from '../IconActionButton';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ButtonWrapper = styled.div`
  button:focus { outline: none; }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ProgramListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(90vh - 310px);
`;

const ProgramCard = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #D0D0D0;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  margin-bottom: 12px;
  gap: 4px;
`;

const ProgramInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  padding-right: 4px;
`;

const InfoValue = styled.div`
  font-size: 12px;
  color: #333333;
  padding-right: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const GroupProgramsForm = ({ groupPrograms, programs, onOpenNestedModal }) => {
  const programsById = useMemo(
    () => programs.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}),
    [programs],
  );

  return (
    <FormWrapper>
      <TabHeader>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 600 }}>
          Program Library Access
        </Text>
        <ButtonWrapper>
          <Button
            name='Add Program'
            icon='add-program'
            bgColor='white'
            borderColor='#ddd'
            txtColor='#333'
            customBorderRadius='4px'
            fullWidth
            styledTextProps={{
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.3px',
            }}
            onClick={(e) => { e.preventDefault(); onOpenNestedModal('addProgram'); }}
          />
        </ButtonWrapper>
      </TabHeader>

      <ProgramListContainer>
        {groupPrograms.length > 0 ? (
          groupPrograms.map((groupProgram) => {
            const catalogProgram = programsById[groupProgram.saveInfoId];
            return (
              <ProgramCard key={groupProgram.saveInfoId}>
                <ProgramInfo>
                  <Text styledTextProps={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                    {catalogProgram?.programName || 'Unknown Program'}
                  </Text>
                  <InfoRow>
                    <InfoLabel>Added:</InfoLabel>
                    <InfoValue>{groupProgram.added || 'Unknown'}</InfoValue>
                    <InfoLabel>Last Activity:</InfoLabel>
                    <InfoValue>{groupProgram.lastActivity || 'No activity yet'}</InfoValue>
                  </InfoRow>
                </ProgramInfo>
                <ButtonContainer>
                  <IconActionButton
                    type='delete'
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenNestedModal('removeProgram', null, {
                        saveInfoId: groupProgram.saveInfoId,
                        programName: catalogProgram?.programName,
                      });
                    }}
                  />
                </ButtonContainer>
              </ProgramCard>
            );
          })
        ) : (
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            No programs assigned to this group
          </Text>
        )}
      </ProgramListContainer>
    </FormWrapper>
  );
};

GroupProgramsForm.propTypes = {
  groupPrograms: PropTypes.arrayOf(PropTypes.shape({
    saveInfoId: PropTypes.number,
    added: PropTypes.string,
    lastActivity: PropTypes.string,
  })),
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    programName: PropTypes.string,
  })),
  onOpenNestedModal: PropTypes.func.isRequired,
};

GroupProgramsForm.defaultProps = {
  groupPrograms: [],
  programs: [],
};

export default GroupProgramsForm;
